import { BtnSecondary } from '@components/buttons';
import { DialogFooter, StandardDialog } from '@components/dialog';
import { showModalForm } from '@components/modal-form';
import { useRef, useState } from 'preact/hooks';
import { ManagedFile, ManagedTag } from './state';

export type TagChanges = { addTagIds: string[]; removeTagIds: string[]; newTagNames: string[] };

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Tags could not be saved. Try again.';
}

function EditTagsDialog({
  tags,
  files,
  onCancel,
  onSubmit,
}: {
  tags: ManagedTag[];
  files: ManagedFile[];
  onCancel(): void;
  onSubmit(changes: TagChanges): Promise<void>;
}) {
  const [changes, setChanges] = useState<Map<string, boolean>>(() => new Map());
  const [newTagNames, setNewTagNames] = useState<string[]>([]);
  const [skippedNewNames, setSkippedNewNames] = useState<Set<string>>(() => new Set());
  const [newName, setNewName] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const pendingSave = useRef(false);
  const fileCount = files.length;
  const assignedCounts = new Map<string, number>();
  for (const file of files) {
    for (const tag of file.tags) {
      assignedCounts.set(tag.id, (assignedCounts.get(tag.id) ?? 0) + 1);
    }
  }
  const visible = tags.filter((tag) => tag.name.toLowerCase().includes(search.toLowerCase()));
  const staged = newTagNames.filter((name) => name.toLowerCase().includes(search.toLowerCase()));
  const includedNewNames = newTagNames.filter((name) => !skippedNewNames.has(name));

  function toggleTag(tagId: string, assigned: number, checked: boolean) {
    const next = new Map(changes);
    const originallyMixed = assigned > 0 && assigned < fileCount;
    if (originallyMixed ? changes.get(tagId) === false : checked === (assigned === fileCount)) {
      next.delete(tagId);
    } else {
      next.set(tagId, checked);
    }
    setChanges(next);
  }

  function toggleNewName(name: string) {
    const next = new Set(skippedNewNames);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    setSkippedNewNames(next);
  }

  function stageName() {
    const name = newName.trim();
    if (!name) {
      setError('Enter a tag name.');
      return;
    }
    const skipped = [...skippedNewNames].find((item) => item.toLowerCase() === name.toLowerCase());
    if (skipped) {
      toggleNewName(skipped);
      setNewName('');
      setSearch('');
      setError('');
      return;
    }
    if (
      [...tags.map((tag) => tag.name), ...newTagNames].some(
        (item) => item.toLowerCase() === name.toLowerCase(),
      )
    ) {
      setError('A tag with this name already exists.');
      return;
    }
    setNewTagNames([...newTagNames, name]);
    setNewName('');
    setSearch('');
    setError('');
  }

  async function save(event: Event) {
    event.preventDefault();
    if (pendingSave.current) {
      return;
    }
    pendingSave.current = true;
    setIsSaving(true);
    setError('');
    try {
      await onSubmit({
        addTagIds: [...changes].filter(([, add]) => add).map(([id]) => id),
        removeTagIds: [...changes].filter(([, add]) => !add).map(([id]) => id),
        newTagNames: includedNewNames,
      });
    } catch (err) {
      pendingSave.current = false;
      setError(errorMessage(err));
      setIsSaving(false);
    }
  }

  function close() {
    if (!pendingSave.current) {
      onCancel();
    }
  }

  return (
    <StandardDialog onClose={close}>
      <form class="flex flex-col gap-4" onSubmit={save}>
        <h2 class="font-semibold text-base">
          {fileCount === 1 ? `Edit tags for ${files[0].name}` : `Edit tags for ${fileCount} files`}
        </h2>
        {fileCount > 1 && (
          <p class="text-sm text-gray-600">
            Tags marked “Some files” stay as they are unless you change them.
          </p>
        )}
        <label class="text-sm font-medium" for="edit-tags-search">
          Search tags
        </label>
        <input
          id="edit-tags-search"
          type="search"
          class="ruz-input"
          placeholder="Search tags"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          disabled={isSaving}
          autoFocus
        />
        <div class="border border-gray-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-gray-200">
          {visible.map((tag) => {
            const assigned = assignedCounts.get(tag.id) ?? 0;
            const checked = changes.get(tag.id) ?? assigned === fileCount;
            const originallyMixed = assigned > 0 && assigned < fileCount;
            const mixed = !changes.has(tag.id) && originallyMixed;
            return (
              <label
                key={tag.id}
                class="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isSaving}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = mixed;
                    }
                  }}
                  onChange={(e) => toggleTag(tag.id, assigned, e.currentTarget.checked)}
                />
                <span class="text-sm text-gray-800">{tag.name}</span>
                {mixed && <span class="ml-auto text-xs text-gray-500">Some files</span>}
              </label>
            );
          })}
          {staged.map((name) => (
            <label
              key={name}
              class="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={!skippedNewNames.has(name)}
                disabled={isSaving}
                onChange={() => toggleNewName(name)}
              />
              <span class="text-sm text-gray-800">{name}</span>
              <span class="ml-auto text-xs text-gray-500">New</span>
            </label>
          ))}
          {visible.length === 0 && staged.length === 0 && (
            <p class="p-3 text-sm text-gray-500">No tags found. Create a tag below.</p>
          )}
        </div>
        <label class="text-sm font-medium" for="edit-tags-new">
          Create tag
        </label>
        <div class="flex flex-wrap gap-2">
          <input
            id="edit-tags-new"
            class="ruz-input flex-1 min-w-40"
            value={newName}
            disabled={isSaving}
            onInput={(e) => {
              setNewName(e.currentTarget.value);
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                stageName();
              }
            }}
            maxLength={255}
            placeholder="Tag name"
          />
          <BtnSecondary type="button" disabled={isSaving} class="rounded-full" onClick={stageName}>
            Create and add
          </BtnSecondary>
        </div>
        <p class="text-xs text-gray-500">New tags are added when you save.</p>
        {error && (
          <p role="alert" class="text-sm text-red-700">
            {error}
          </p>
        )}
        <DialogFooter
          onClose={close}
          isLoading={isSaving}
          confirmButtonText="Save tags"
          isConfirmDisabled={isSaving || (changes.size === 0 && includedNewNames.length === 0)}
        />
      </form>
    </StandardDialog>
  );
}

export function promptEditTags(
  tags: ManagedTag[],
  files: ManagedFile[],
  onSave: (changes: TagChanges) => Promise<void>,
): Promise<void> {
  return showModalForm<void>(({ resolve }) => (
    <EditTagsDialog
      tags={tags}
      files={files}
      onCancel={() => resolve()}
      onSubmit={async (changes) => {
        await onSave(changes);
        resolve();
      }}
    />
  ));
}
